<template>
  <nuxt-link
    :class="['flex snap-center flex-col gap-2 font-Spectral md:gap-4', forSingleContent ? 'w-full' : '']"
    :data-testid="`featured-card-${featured.id}`"
    :to="detailPageLink"
    @click="handleClick"
  >
    <div class="group relative h-[350px] w-full min-w-[280px] overflow-hidden md:h-[400px] md:min-w-[320px]">
      <!-- PCでは横長、SPでは縦長のKV画像を表示する。画像なしは静的アセットの no-image を表示する -->
      <OptimizedImage
        v-if="featured.backgroundWideMedias.length > 0"
        loading="lazy"
        :alt="featured.titleEn"
        :class="['hidden h-full w-full object-cover transition-all duration-500 md:block md:group-hover:scale-[102%] md:group-hover:opacity-90']"
        :image-url="featured.backgroundWideMedias[0].url"
        :optimized-image-options="{ width: FEATURED_CARD_IMAGE_WIDTH }"
      />
      <img
        v-else
        alt="no-image"
        class="hidden h-full w-full object-cover transition-all duration-500 md:block md:group-hover:scale-[102%] md:group-hover:opacity-90"
        loading="lazy"
        src="@/assets/img/no-image.svg"
      />
      <OptimizedImage
        v-if="featured.backgroundTallMedias.length > 0"
        loading="lazy"
        :alt="featured.titleEn"
        :class="['block h-full w-full object-cover transition-all duration-500 md:hidden md:group-hover:scale-[102%] md:group-hover:opacity-90']"
        :image-url="featured.backgroundTallMedias[0].url"
        :optimized-image-options="{ width: FEATURED_CARD_IMAGE_WIDTH }"
      />
      <img
        v-else
        alt="no-image"
        class="block h-full w-full object-cover transition-all duration-500 md:hidden md:group-hover:scale-[102%] md:group-hover:opacity-90"
        loading="lazy"
        src="@/assets/img/no-image.svg"
      />
      <!-- グラデーションオーバーレイを追加 -->
      <div class="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/50 to-transparent"></div>
      <div v-if="type === 'destination'" class="absolute inset-0 flex flex-col items-center pt-[110px] text-center text-white md:pt-[130px]">
        <p class="font-Spectral text-3xl">{{ featured.titleEn }}</p>
        <div class="vertical-text mt-4 flex items-center gap-2 text-sm md:mt-6">
          <div class="h-6 w-[0.5px] -translate-x-1/2 bg-white md:h-8"></div>
          <p>{{ featured.titleJa }}</p>
          <div class="h-6 w-[0.5px] -translate-x-1/2 bg-white md:h-8"></div>
        </div>
      </div>
      <div class="absolute bottom-0 left-0 w-full p-2 text-white">
        <p v-if="type !== 'destination'">{{ featured.titleEn }}</p>
        <div class="flex items-center justify-end">
          <div :class="['mr-1 hidden h-[0.5px] w-16 bg-white', isDisplayViewMore ? 'md:block' : '']"></div>
          <p class="text-sm">view more</p>
        </div>
      </div>
    </div>
  </nuxt-link>
</template>

<script lang="ts" setup>
import { OptimizedImage } from '@/wabunka-src/common/components';
import { generateDetailPageLink, type FeaturedType, type MinimumFeaturedViewModel } from '@/wabunka-src/features/Featured';

// カードのKV画像の変換後横幅（h-[350px]/[400px] のカードを高DPRでも十分な解像度で表示できるサイズ）
const FEATURED_CARD_IMAGE_WIDTH = 720;

interface Props {
  featured: MinimumFeaturedViewModel;
  type: FeaturedType;
  isDisplayViewMore: boolean;
  forSingleContent: boolean;
  onFeaturedClick?: (type: string, id: string) => void;
}

const props = defineProps<Props>();

const detailPageLink = computed(() => {
  return generateDetailPageLink(props.featured);
});

const handleClick = () => {
  if (props.onFeaturedClick) {
    props.onFeaturedClick(`featured-${props.type}`, props.featured.id);
  }
};
</script>

<!-- tailwindの範囲外のSCSS -->
<style lang="scss" scoped>
.vertical-text {
  writing-mode: vertical-rl;
  text-orientation: mixed;
  -webkit-writing-mode: vertical-rl;
  -ms-writing-mode: tb-rl;
  -webkit-text-orientation: mixed;
  text-combine-upright: none;
  -webkit-text-combine-upright: none;
}
</style>
